<template>
  <v-card light>
    <v-toolbar flat>
      <div>
        <v-toolbar-title class="headline">
          Bulk Payout Results
        </v-toolbar-title>
        <p class="mb-2">If the Result is Failed, click the link to view the Failed rows.</p>
      </div>
      <v-spacer></v-spacer>
      <v-form @submit.stop.prevent="onSubmit">
        <v-text-field
          class="mr-2"
          v-model="search"
          :label="$i18n.translate('Filter')"
          flat
          solo-inverted
          hide-details
          clearable
          clear-icon="mdi-close-circle-outline"
          @click:clear="onClear"
        ></v-text-field>
      </v-form>
    </v-toolbar>

    <v-card-text>
      <ApiError :errors="errors"></ApiError>
      <v-data-table
        :headers="headers"
        :items="batches"
        :server-items-length="total"
        :loading="loading"
        :options.sync="options"
        class="elevation-1"
      >
        <template v-slot:item.id="{ item }">
          <div>#{{ item.id }}</div>
          <div class="text-caption grey--text" v-if="getTemplateName(item)">Template: {{ getTemplateName(item) }}</div>
        </template>
        <template v-slot:item.createdDate="{ item }">
          {{ item.createdDate | formatDateClient("MM/DD/YYYY", selectedClient) }}<br />
          ({{ item.createdDate | formatDateFromNow }})
        </template>
        <template v-slot:item.result="{ item }">
          <div class="d-flex align-center">
            <v-icon :color="getStatusColor(item.status)" small class="mr-1">{{ getStatusIcon(item.status) }}</v-icon>
            <span>{{ getStatusLabel(item.status) }}</span>
          </div>
          <div class="text-caption grey--text">
            {{ item.successfulRows || 0 }} completed &middot; {{ item.failedRows || 0 }} failed &middot;
            {{ item.totalRows || 0 }} total
            <template v-if="item.failedRows > 0">
              &middot; <a href="#" @click.prevent="viewBatchDetail(item)">View failed rows</a>
            </template>
          </div>
        </template>
        <template v-slot:item.file="{ item }">
          <v-tooltip top v-if="item.upload && item.upload.href">
            <template v-slot:activator="{ on, attrs }">
              <a
                :href="item.upload.href"
                target="_blank"
                rel="noreferrer"
                class="d-inline-flex align-center text-truncate"
                style="max-width: 220px;"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small class="mr-1">mdi-download</v-icon>
                <span class="text-truncate">{{ item.upload.originalFilename }}</span>
              </a>
            </template>
            <span>{{ item.upload.originalFilename }}</span>
          </v-tooltip>
          <span v-else>&mdash;</span>
        </template>
      </v-data-table>
      <v-dialog v-model="dialog">
        <v-card>
          <v-toolbar>
            <v-toolbar-title>Viewing Error Details for Batch Id {{ batchId }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click="dialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <BulkPayoutBatch class="mt-5" :batchId="batchId" v-if="batchId" />
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-card-text>
  </v-card>
</template>

<script>
import BulkPayoutBatch from "./BulkPayoutBatch.vue";
import { mapGetters } from "vuex";
import ApiError from "@/gapp-components/components/display/ApiError.vue";
import BulkPayoutBatchService from "@/gapp-components/services/bulkPayoutBatch.service";

export default {
  name: "BulkPayoutBatches",
  components: {
    ApiError,
    BulkPayoutBatch
  },
  data() {
    return {
      dialog: false,
      batchId: null,
      search: "",
      loading: false,
      errors: {},
      headers: [
        { text: "Batch ID", value: "id" },
        { text: "Submitted By", value: "submittedByParticipant.fullName", sortable: false },
        { text: "Source File", value: "file", sortable: false },
        { text: "Result", value: "result", sortable: false },

        { text: "Date", value: "createdDate", sortable: true }
      ],

      batches: [],
      total: 0,
      options: {
        itemsPerPage: 10,
        page: 1,
        sortBy: ["createdDate"],
        sortDesc: [true]
      }
    };
  },
  computed: {
    ...mapGetters(["selectedClient"])
  },
  watch: {
    options: {
      handler: "getData",
      deep: true
    }
  },
  methods: {
    viewBatchDetail(item) {
      this.batchId = item.id;
      this.dialog = true;
    },
    getTemplateName(item) {
      return item.bulkPayoutTemplate?.name || "";
    },
    onSubmit() {
      this.options.page = 1;
      this.getData();
    },

    onClear() {
      this.search = "";
      this.getData();
    },

    async getData() {
      this.loading = true;
      try {
        let filters = {};
        if (this.search && this.search.length > 0) {
          filters.keyword = this.search;
        }

        const { data: response } = await BulkPayoutBatchService.search(filters, this.options, [
          "submittedByParticipant",
          "upload",
          "bulkPayoutTemplate"
        ]);
        this.batches = response.content || [];
        this.total = response.totalElements || 0;
      } catch (error) {
        this.errors = this.$api.getErrorsFromResponse(error);
      } finally {
        this.loading = false;
      }
    },
    getStatusText(status) {
      if (!status) return "";
      if (typeof status === "object") {
        return status.description || status.name || "";
      }
      return status;
    },
    getStatusLabel(status) {
      const statusName = typeof status === "object" && status !== null ? status.name : status;
      switch (statusName) {
        case "FAILED":
          return "Failed";
        case "COMPLETED_WITH_ERRORS":
          return "Completed with errors";
        case "COMPLETED":
          return "Completed";
        default:
          return this.getStatusText(status);
      }
    },
    getStatusIcon(status) {
      const statusName = typeof status === "object" && status !== null ? status.name : status;
      switch (statusName) {
        case "FAILED":
          return "mdi-alert-circle";
        case "COMPLETED_WITH_ERRORS":
          return "mdi-alert";
        case "COMPLETED":
          return "mdi-check-circle";
        default:
          return "mdi-information";
      }
    },
    getStatusColor(status) {
      const statusName = typeof status === "object" && status !== null ? status.name : status;
      switch (statusName) {
        case "FAILED":
          return "red";
        case "COMPLETED_WITH_ERRORS":
          return "orange";
        case "COMPLETED":
          return "green";
        default:
          return "grey";
      }
    }
  }
};
</script>
